import React, { useState, useEffect, useMemo, memo } from 'react';
import classNames from 'classnames';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { fetchCurrentUserData, selectAccountLoading } from '@entities/account';
import { useAppDispatch, useAppSelector } from '@shared/store';
import SelectableList from 'application/components/SelectableList';
import PointItemContent from 'application/components/PointItemContent';
import EmptyState from 'application/components/EmptyState';
import AddressTagFilter from 'application/components/AddressTagFilter';
import { IdType } from 'application/pages/types';
import { VideoPriceInfo } from 'application/components/VideoPricePlan';

import { PointShortTypeFragment } from '@shared/api/graphql/generated';

import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import Switcher, { LabelPlacement } from '@zvuk-b2b/react-uikit/ui/Switcher';

import { usePointsTabPresenter } from '../presenters/PointsTabPresenter';
import { useVideoStreamsPointsController } from '../controllers/VideoStreamsPointsController';
import { TEXTS, VIDEO_STREAM_TAG_FILTER_TEXTS } from '../../texts';
import { ActionButtons } from './components';
import { DATA_TEST_IDS, VIDEO_STREAM_TAG_FILTER_TEST_IDS, VIDEO_TEMP_FF } from './consts';
import { getUniqueAddressTags, filterPointsByAddressAndTags } from './utils';

import styles from './VideoStreamBindModal.module.scss';

const VideoStreamBindModal: React.FC<ModalProps[ModalTypes.VIDEO_STREAM_BIND]> = ({
  selectedPoints: initialSelectedPoints,
  videoStreamId,
  close,
  openModal,
  replaceModal,
  selectedPoint,
  isDenyCreatePoint,
  setCurrentSelectedPointPoint,
}) => {
  const dispatch = useAppDispatch();

  const isAccountLoading = useAppSelector(selectAccountLoading);

  const { allPointList, loadMore } = usePointsTabPresenter({ isVideoDemo: null });

  const { isIpad } = useMedia();

  const { selectedPoints, onPointsChange, isButtonDisabled } = useVideoStreamsPointsController({
    initialSelectedPoints,
    points: allPointList.points || [],
  });

  const [search, setSearch] = useState<string>(selectedPoint?.pointAddress || '');
  const [isSelectedPointSet, setIsSelectedPointSet] = useState<boolean>(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isExactSearch, setIsExactSearch] = useState(true);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchCurrentUserData());
  }, []);

  const isAllPointsLoading = allPointList.loading || allPointList.isReloading || isAccountLoading;

  const allPoints = allPointList.points || [];
  const addressTags = useMemo(() => getUniqueAddressTags(allPoints), [allPoints]);
  const currentItems = useMemo(
    () => filterPointsByAddressAndTags(allPoints, search, selectedTagIds, isExactSearch),
    [allPoints, isExactSearch, search, selectedTagIds]
  );
  const isEmpty = allPointList.points?.length === 0;
  const isEmptyFiltered = !isAllPointsLoading && allPoints.length > 0 && currentItems.length === 0;

  const selectedItems = useMemo(() => {
    const result = new Map<string, IdType>();

    selectedPoints.forEach((point) => {
      result.set(point.id, { id: point.id });
    });

    return result;
  }, [selectedPoints]);

  const hasSelectedItems = selectedItems.size > 0;
  const hasUnselectedItems = currentItems.some((item) => !selectedItems.has(item.id));

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  const onChangeWrapper = (value: boolean, point: PointShortTypeFragment) => {
    setIsTagFilterOpen(false);

    if (value) {
      onPointsChange([...selectedPoints, point]);
    } else {
      onPointsChange(selectedPoints.filter((current) => current.id !== point.id));
    }
  };

  const onChangeAll = (value: boolean) => {
    if (value) {
      const selectedPointIds = new Set([...selectedItems.keys(), ...currentItems.map((point) => point.id)]);

      onPointsChange(allPoints.filter((point) => selectedPointIds.has(point.id)));
      return;
    }

    const filteredItems = selectedPoints.filter((current) => !currentItems.some((item) => item.id === current.id));

    onPointsChange(filteredItems);
  };

  useEffect(() => {
    if (selectedPoint && currentItems.length && !isSelectedPointSet) {
      const currentPoint = currentItems.find((item) => item.id === selectedPoint?.pointId);

      setIsSelectedPointSet(true);
      if (currentPoint) {
        onChangeWrapper(true, currentPoint);
      }
    }
  }, [selectedPoint?.pointId, currentItems, isSelectedPointSet]);

  useEffect(() => {
    if (!search && selectedPoint && isSelectedPointSet) {
      setCurrentSelectedPointPoint?.(undefined);
    }
  }, [search, selectedPoint?.pointId, isSelectedPointSet]);

  return (
    <ModalScreen
      title={TEXTS.MODAL_BIND_TITLE}
      titlePlacement="left"
      disableScroll={!isIpad}
      contentClassName={classNames(styles.content, {
        [styles.contentEmpty]: VIDEO_TEMP_FF && isEmpty,
      })}
      onClose={close}
      actionButtons={
        <ActionButtons
          videoStreamId={videoStreamId}
          isBindButtonDisabled={isButtonDisabled}
          isDenyCreatePoint={isDenyCreatePoint}
          withDemoPeriod={false}
          showRequestVideoButton={VIDEO_TEMP_FF && isEmpty}
          selectedPoints={selectedPoints}
          selectedPoint={selectedPoint}
          allPointsIds={(allPointList.points || []).map((point) => point.id)}
          openModal={openModal}
          replaceModal={replaceModal}
          close={close}
        />
      }>
      {VIDEO_TEMP_FF && isEmpty ? (
        <div>
          <div className={styles.noAddress}>{TEXTS.MODAL_BIND_NO_ADDRESS}</div>
          <VideoPriceInfo />
        </div>
      ) : (
        <>
          <div className={styles.addressTagFilter}>
            <AddressTagFilter
              tags={addressTags}
              selectedTagIds={selectedTagIds}
              isExactSearch={isExactSearch}
              isOpen={isTagFilterOpen}
              search={search}
              isLoading={isAllPointsLoading}
              texts={VIDEO_STREAM_TAG_FILTER_TEXTS}
              testIds={VIDEO_STREAM_TAG_FILTER_TEST_IDS}
              onExactSearchChange={setIsExactSearch}
              onOpenChange={setIsTagFilterOpen}
              onSearchChange={onSearchChange}
              onSelectedTagIdsChange={setSelectedTagIds}
            />
          </div>

          {!isEmpty && !isEmptyFiltered && (
            <SelectableList<PointShortTypeFragment>
              elementId="checkbox_item_bind"
              listWrapperClassName={classNames(styles.listWrapper, {
                [styles.listWrapperLoading]: isAllPointsLoading,
              })}
              items={currentItems}
              itemKeyForRender="address"
              itemContent={
                <PointItemContent
                  showVideoStreamData
                  isLoading={isAllPointsLoading}
                  hintText={TEXTS.MODAL_BIND_DISABLED_POINT_HINT_TEXT}
                  hintIconNearSelector
                />
              }
              labelPlacement={LabelPlacement.LEFT}
              itemsLoading={isAllPointsLoading}
              isLoading={isAllPointsLoading}
              selectorDisabled={isAllPointsLoading}
              selectedItems={selectedItems}
              onChange={onChangeWrapper}
              loadMore={loadMore}
              paginationInfo={allPointList.paginationInfo}
              totalItemsCount={allPointList.points?.length || 0}
              hasMultiselect
              hasSelectedItems={hasSelectedItems}
              hasUnSelectedItems={hasUnselectedItems}
              onChangeAll={onChangeAll}
              selectorRenderer={Switcher}
              headerClassName={styles.listHeader}
              headerLeftSlot={null}
            />
          )}

          {isEmpty && (
            <EmptyState>
              <span>{TEXTS.POINTS_LIST_EMPTY}</span>
            </EmptyState>
          )}

          {isEmptyFiltered && (
            <EmptyState>
              <span data-testid={DATA_TEST_IDS.POINTS_LIST_EMPTY}>{TEXTS.POINTS_LIST_FILTERED_EMPTY}</span>
            </EmptyState>
          )}
        </>
      )}
    </ModalScreen>
  );
};

export default memo(VideoStreamBindModal);
